import { useMemo, useState } from "react";
import { localDateString, summarizeTrip } from "../domain";
import type { ActivityEntry, Profile, Trip } from "../types";
import type { AccountSession } from "../auth";
import type { SyncStatus } from "../useAccountSync";
import { AgentPanel } from "./AgentPanel";
import { AppHeader } from "./AppHeader";
import { ConfirmDialog } from "./Dialog";
import { Icon } from "./Icon";

interface DashboardProps {
  profile: Profile;
  trips: Trip[];
  activity: ActivityEntry[];
  onCreate: () => void;
  onOpen: (tripId: string) => void;
  onEdit: (trip: Trip) => void;
  onDelete: (tripId: string) => void;
  onProfile: () => void;
  account: AccountSession | null;
  accountPending: boolean;
  syncStatus: SyncStatus;
  onAccount: () => void;
}

function formatRange(trip: Trip): string {
  if (!trip.startDate || !trip.endDate) return `${trip.durationDays} day${trip.durationDays === 1 ? "" : "s"} · Dates not set`;
  const start = new Date(`${trip.startDate}T00:00:00`);
  const end = new Date(`${trip.endDate}T00:00:00`);
  const formatter = new Intl.DateTimeFormat("en", { month: "short", day: "numeric" });
  return `${formatter.format(start)} – ${formatter.format(end)}, ${end.getFullYear()}`;
}

function TripList({ title, trips, currency, onOpen, onEdit, onDelete }: {
  title: string;
  trips: Trip[];
  currency: string;
  onOpen: (tripId: string) => void;
  onEdit: (trip: Trip) => void;
  onDelete: (trip: Trip) => void;
}) {
  if (!trips.length) return null;
  const headingIcon = title === "Upcoming" ? "calendar" : title === "Past" ? "clock" : "route";
  return (
    <section className="trip-section" aria-labelledby={`trip-group-${title.toLowerCase()}`}>
      <div className="section-heading"><h2 id={`trip-group-${title.toLowerCase()}`}><Icon name={headingIcon} size={18} />{title}</h2><span>{trips.length}</span></div>
      <div className="trip-list">
        {trips.map((trip) => {
          const summary = summarizeTrip(trip, currency);
          return (
            <article className="trip-row" key={trip.id}>
              <button type="button" className="trip-open" onClick={() => onOpen(trip.id)}>
                <span className="trip-sequence" aria-hidden="true"><Icon name="route" size={19} /></span>
                <span className="trip-main"><strong>{trip.name}</strong><small>{trip.destination}</small></span>
                <span className="trip-data"><small>{trip.startDate ? "Dates" : "Length"}</small><strong>{formatRange(trip)}</strong></span>
                <span className="trip-data"><small>Route</small><strong>{summary.stopCount ? `${summary.stopCount} stop${summary.stopCount === 1 ? "" : "s"}` : "Needs planning"}</strong></span>
                <span className="trip-data trip-cost"><small>Estimate</small><strong>{summary.totalEstimatedCost ? `${currency} ${summary.totalEstimatedCost.toLocaleString()}` : "Not set"}</strong></span>
                <Icon name="arrow-right" size={18} />
              </button>
              <div className="trip-row-actions">
                <button className="icon-button" type="button" onClick={() => onEdit(trip)} aria-label={`Edit ${trip.name}`}><Icon name="edit" /></button>
                <button className="icon-button danger-icon" type="button" onClick={() => onDelete(trip)} aria-label={`Delete ${trip.name}`}><Icon name="trash" /></button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function Dashboard({
  profile,
  trips,
  activity,
  onCreate,
  onOpen,
  onEdit,
  onDelete,
  onProfile,
  account,
  accountPending,
  syncStatus,
  onAccount,
}: DashboardProps) {
  const [deleteTarget, setDeleteTarget] = useState<Trip | null>(null);
  const sortedTrips = useMemo(() => [...trips].sort((left, right) => right.updatedAt.localeCompare(left.updatedAt)), [trips]);
  const today = localDateString(new Date());
  const datedTrips = sortedTrips.filter((trip) => trip.startDate && trip.endDate);
  const undated = sortedTrips.filter((trip) => !trip.startDate || !trip.endDate);
  const upcoming = datedTrips.filter((trip) => trip.endDate! >= today);
  const past = datedTrips.filter((trip) => trip.endDate! < today);
  const summaries = sortedTrips.map((trip) => summarizeTrip(trip, profile.currency));
  const totalStops = summaries.reduce((sum, summary) => sum + summary.stopCount, 0);
  const needsPlanning = summaries.filter((summary) => summary.stopCount === 0).length;
  const agentChanges = activity.filter((entry) => entry.source === "agent").length;

  return (
    <div className="app-shell">
      <AppHeader profile={profile} account={account} accountPending={accountPending} syncStatus={syncStatus} onAccount={onAccount} onHome={() => undefined} onProfile={onProfile} />
      <main id="main" className="dashboard">
        <header className="page-heading">
          <div><h1>Trips</h1><p>Open a route, plan the next day, or start somewhere new.</p></div>
          <button className="button button-primary" type="button" onClick={onCreate}><Icon name="plus" /> Create trip</button>
        </header>

        <section className="workspace-overview" aria-label="Trip overview">
          <div><span className="overview-label"><Icon name="calendar" size={17} />Upcoming</span><strong>{upcoming.length}</strong></div>
          <div><span className="overview-label"><Icon name="route" size={17} />Needs a route</span><strong>{needsPlanning}</strong></div>
          <div><span className="overview-label"><Icon name="map" size={17} />Planned stops</span><strong>{totalStops}</strong></div>
          <div><span className="overview-label"><Icon name="spark" size={17} />Agent changes</span><strong>{agentChanges}</strong></div>
        </section>

        <div className="dashboard-workspace">
          <div className="trip-workspace">
            {sortedTrips.length === 0 ? (
              <section className="empty-trips">
                <span className="empty-icon"><Icon name="map" size={26} /></span>
                <h2>Create your first route.</h2>
                <p>Add the trip frame yourself or ask your browser agent to create it.</p>
                <button className="button button-primary" type="button" onClick={onCreate}><Icon name="plus" /> Create trip</button>
                <code>“Plan a balanced 4-day trip to Kyoto.”</code>
              </section>
            ) : (
              <>
                <TripList title="Upcoming" trips={upcoming} currency={profile.currency} onOpen={onOpen} onEdit={onEdit} onDelete={setDeleteTarget} />
                <TripList title="Past" trips={past} currency={profile.currency} onOpen={onOpen} onEdit={onEdit} onDelete={setDeleteTarget} />
                <TripList title="Dates not set" trips={undated} currency={profile.currency} onOpen={onOpen} onEdit={onEdit} onDelete={setDeleteTarget} />
              </>
            )}
          </div>

          <aside className="dashboard-activity" aria-label="Activity">
            <AgentPanel activity={activity} />
          </aside>
        </div>
      </main>

      <footer className="app-footer">
        <span>Roam by <a href="https://codepawl.com" target="_blank" rel="noreferrer">CodePawl <Icon name="external" size={13} /></a></span>
        <span><a href="https://github.com/codepawl/roam" target="_blank" rel="noreferrer">Source and AGPL-3.0 license <Icon name="external" size={13} /></a> · Local-first · Optional account sync · OpenStreetMap place data</span>
      </footer>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete this trip?"
        description={deleteTarget ? `${deleteTarget.name} and all of its stops will be removed from this browser. This can be undone until the page closes.` : ""}
        confirmLabel="Delete trip"
        destructive
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && onDelete(deleteTarget.id)}
      />
    </div>
  );
}
